/**
 * THE MOVE FLOW: the form that records a change to one visit and asks how the client will know.
 * It carries CLAUDE.md binding rule 7, the calendar changes last, and the header of
 * `../VisitDetail.tsx` says why the form asks its two questions in that order.
 *
 * A TEXT GOES ONLY PAST OPS'S OWN GO-LIVE SWITCH (ops #31), off until an owner moves it. On the text
 * path the form says where it stands, with the phone system's newest word beneath, and where a text
 * to her number would not go it offers the text disabled and names the switch (`TextingGate`); email
 * and the typed reason stay, and until somebody chooses, the typed reason is the path offered. The
 * server parks a text the switch refuses and leaves the change open, and the form says so if one
 * gets that far.
 *
 * AN EMAIL GOES ONLY PAST OPS'S SWITCH FOR CLIENT EMAIL (ops #76 O6), and home delivers it: on the
 * email path the form says where that switch stands and offers the email disabled where her address
 * would not go, naming it (`EmailGate`), the typed reason the way on, as it does for a text.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  CHARGE_DECISIONS,
  CHARGE_DECISION_LABELS,
  SILENCE_REASONS,
  SILENCE_REASON_LABELS,
  VISIT_CHANGE_KIND_LABELS,
  humanise,
  type ChargeDecision,
  type SilenceReason,
  type VisitChangeKind,
} from '@fk/shared';
import {
  ApiError,
  listCleaners,
  previewMessage,
  recordChange,
  recordConsent,
  type CleanerRow,
  type ContactRow,
  type Me,
  type MessagePreview,
  type NewChange,
  type VisitRow,
} from '../../api.ts';
import { offeredForAClient } from '../../checks.ts';
import { choiceLineOf } from '../../choices.ts';
import { Card } from '../../components/AppShell.tsx';
import { Button, SelectField, TextAreaField, TextField } from '../../components/Field.tsx';
import { useWideView } from '../../components/ListView.tsx';
import { EmailGate, emailPath } from '../../components/EmailGate.tsx';
import { Busy, ErrorNotice } from '../../components/Notice.tsx';
import { TextingGate, textingPath } from '../../components/TextingGate.tsx';
import { TEXT_CONSENT_NOTE } from '../../consent.ts';
import { isDay, isTimeOfDay, orNull, timeOfDayOf } from '../../format.ts';
import { MESSAGE_CHANNEL_LABELS } from '../../labels.ts';
import { HowChoice, TimeField } from './MoveFields.tsx';
import { PAIR, outcomeFor, type Outcome } from './shared.ts';

/** What the office may say happened to one visit. A pause is recorded on the series, not here. */
const ACTIONS: readonly VisitChangeKind[] = [
  'moved',
  'window_changed',
  'cleaner_changed',
  'cancelled_by_client',
  'cancelled_by_us',
];

const SEND_CHANNELS = ['sms', 'email'] as const;
type SendChannel = (typeof SEND_CHANNELS)[number];

/**
 * THE CLIENT'S CHOICE AFTER A CLEANER CANNOT COME (ops #85): the three a call-off is answered by, a new
 * slot, one named substitute, or skipped at no charge, and nothing else, since the server refuses a
 * window change or the client's own cancellation as a choice.
 */
export const CALL_OFF_ACTIONS: readonly VisitChangeKind[] = [
  'moved',
  'cleaner_changed',
  'cancelled_by_us',
];

/** The call-off a move answers, and which of its three choices the office pressed. */
export interface CallOffChoiceStart {
  readonly id: string;
  readonly kind: (typeof CALL_OFF_ACTIONS)[number];
}

/** Which of the two answers to "how will she know" a press is committing to. */
type Path = 'message' | 'silence';

export function MoveFlow({
  me,
  visit,
  clientId,
  contacts,
  onChanged,
  callOff,
}: {
  me: Me;
  visit: VisitRow;
  clientId: string;
  contacts: readonly ContactRow[];
  onChanged: (outcome: Outcome) => void;
  /** The call-off this move answers (ops #85); its choice is carried with `callOffId`. */
  callOff?: CallOffChoiceStart;
}) {
  const wide = useWideView();

  // A visit nobody could get into is closed to every change but the one that puts it back on the
  // calendar; the server refuses the rest, so the rest are not offered.
  const noAccess = visit.status === 'no_access';
  const actions: readonly VisitChangeKind[] = callOff
    ? CALL_OFF_ACTIONS
    : noAccess
      ? ['no_access_rescheduled']
      : ACTIONS;
  const currentStart = timeOfDayOf(visit.windowStart) ?? '';
  const currentEnd = timeOfDayOf(visit.windowEnd) ?? '';

  const [kind, setKind] = useState<VisitChangeKind>(callOff?.kind ?? actions[0] ?? 'moved');
  const [toDate, setToDate] = useState('');
  const [windowStart, setWindowStart] = useState(currentStart);
  const [windowEnd, setWindowEnd] = useState(currentEnd);
  const [cleanerId, setCleanerId] = useState('');
  const [chargeDecision, setChargeDecision] = useState<ChargeDecision | ''>('');
  const [note, setNote] = useState('');
  const [how, setHow] = useState<Path>(me.canHandOffMessages ? 'message' : 'silence');
  const [contactId, setContactId] = useState('');
  const [channel, setChannel] = useState<SendChannel | ''>('');
  const [body, setBody] = useState('');
  /** The sentence the server last offered and what it was about, so her own words stand apart. */
  const offered = useRef<{ readonly body: string; readonly about: string } | null>(null);
  const [preview, setPreview] = useState<MessagePreview | null>(null);
  const [previewProblem, setPreviewProblem] = useState<string | null>(null);
  const [silenceReason, setSilenceReason] = useState<SilenceReason | ''>('');
  const [crew, setCrew] = useState<readonly CleanerRow[] | null>(null);
  const [crewProblem, setCrewProblem] = useState<string | null>(null);
  const [crewAttempt, setCrewAttempt] = useState(0);
  const [problems, setProblems] = useState<readonly string[]>([]);
  const [failure, setFailure] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  /** Set the moment somebody picks a path, so the consent answer never overrides her choice. */
  const [pathChosen, setPathChosen] = useState(false);
  const [consentWords, setConsentWords] = useState('');
  const [consentAttempt, setConsentAttempt] = useState(0);
  const [consentProblem, setConsentProblem] = useState<string | null>(null);
  const [recordingConsent, setRecordingConsent] = useState(false);

  const isMove = kind === 'moved' || kind === 'no_access_rescheduled';
  const isWindow = kind === 'window_changed';
  const isCleaner = kind === 'cleaner_changed';
  const isCancel = kind === 'cancelled_by_client' || kind === 'cancelled_by_us';
  const windowValid = isTimeOfDay(windowStart) && isTimeOfDay(windowEnd);
  const windowMoved = windowValid && (windowStart !== currentStart || windowEnd !== currentEnd);
  const sendsWindow = isWindow ? windowValid : isMove ? windowMoved : false;

  // Both answers stand side by side on a desk. On a phone the one she chose is the one drawn, and
  // the other costs neither space under her thumb nor a request over mobile data.
  const showMessage = me.canHandOffMessages && (wide || how === 'message');
  const showSilence = wide || how === 'silence';

  useEffect(() => {
    if (!isCleaner || crew !== null) return;
    let live = true;
    setCrewProblem(null);
    listCleaners()
      .then((answer) => {
        // Whoever is put on a client's visit carries the owners' mark that the office's checks are
        // complete, a substitute or not (ops #85, 2026-10-22); the server refuses anybody else.
        if (live) setCrew(answer.cleaners.filter((cleaner) => offeredForAClient(cleaner)));
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setCrewProblem(thrown instanceof ApiError ? thrown.message : 'The crew did not load.');
      });
    return () => {
      live = false;
    };
  }, [isCleaner, crew, crewAttempt, callOff]);

  /**
   * The default sentence, refetched when what it describes changes, a third of a second after the
   * last keystroke so a date being typed does not cost a request per digit. What comes back
   * replaces the body: the sentence is about the new date, and the old one was about the old one.
   * The channel is asked for too, since a text ends with the opt-out line and an email does not
   * (ops #23); until somebody chooses, the server writes for her own way.
   *
   * A CHANGE OF CHANNEL ALONE NEVER REWRITES WORDS SOMEBODY WROTE. It changes nothing the sentence
   * is about, only its last line, so the new sentence is taken only while the words are still the
   * ones the server last offered, or empty; words she wrote are hers, as the pay link's form keeps
   * them (`switchTo` in `InvoiceDetail.tsx`). She may be choosing email precisely because the
   * register says no to texts, a moment before she approves what she wrote.
   */
  useEffect(() => {
    if (!showMessage) return;
    const asked = {
      kind,
      toDate: isMove && isDay(toDate) ? toDate : null,
      windowStartLocal: sendsWindow ? windowStart : null,
      windowEndLocal: sendsWindow ? windowEnd : null,
      contactId: contactId || null,
    };
    // Everything the sentence is about, which is everything asked but the channel.
    const about = JSON.stringify([asked, consentAttempt]);
    let live = true;
    const timer = setTimeout(() => {
      setPreviewProblem(null);
      previewMessage(visit.id, { ...asked, channel: channel || null })
        .then((answer) => {
          if (!live) return;
          setPreview(answer);
          const last = offered.current;
          offered.current = { body: answer.body, about };
          setBody((words) =>
            last !== null && last.about === about && words.trim() !== '' && words !== last.body
              ? words
              : answer.body,
          );
        })
        .catch((thrown: unknown) => {
          if (!live) return;
          setPreviewProblem(
            thrown instanceof ApiError ? thrown.message : 'The default sentence did not load.',
          );
        });
    }, 300);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [
    showMessage,
    visit.id,
    kind,
    toDate,
    windowStart,
    windowEnd,
    sendsWindow,
    isMove,
    contactId,
    channel,
    consentAttempt,
  ]);

  /** Until the person chooses, the server's suggestion stands: the primary contact, her channel. */
  const effectiveContactId = contactId || preview?.contact?.id || '';
  const effectiveChannel: SendChannel = channel || (preview?.channel === 'email' ? 'email' : 'sms');
  const chosenContact = contacts.find((contact) => contact.id === effectiveContactId) ?? null;

  /**
   * WHAT THE CONSENT REGISTER SAYS, BEFORE THE SENTENCE IS WRITTEN RATHER THAN AFTER IT IS SENT.
   * The server refuses a message to somebody who has not agreed to that channel, and it used to
   * refuse it only once the person had chosen the contact, edited the words and pressed send: the
   * change was recorded, the message was parked dead and the visit had not moved. The register
   * answers on the preview now, so the screen offers the path that can succeed.
   */
  const consentOf = preview?.contacts.find((row) => row.id === effectiveContactId) ?? null;
  const mayMessageNow = consentOf
    ? effectiveChannel === 'email'
      ? consentOf.mayEmail
      : consentOf.maySms
    : (preview?.mayMessage ?? false);
  const channelWord = effectiveChannel === 'email' ? 'emails' : 'texts';
  // And what their choices would do to it at this home (ops #76 phase 2), or null where it goes.
  const chosenLine = choiceLineOf(consentOf?.choice?.[effectiveChannel]);

  /**
   * WHETHER OPS'S GO-LIVE SWITCH LETS A TEXT TO HER NUMBER GO (ops #31). A text it would not let go
   * is offered disabled, and the button says nothing different: the line above it says why.
   */
  const texting = textingPath(me.smsGoLive, chosenContact?.phoneE164 ?? null);
  const textHeld = effectiveChannel === 'sms' && !texting.open;
  /**
   * AND WHETHER OPS'S SWITCH FOR CLIENT EMAIL LETS AN EMAIL TO HER ADDRESS GO (ops #76 O6), its twin:
   * an email it would not let go is offered disabled in the same way, the line above it saying why.
   */
  const emailing = emailPath(me.emailGoLive, chosenContact?.email ?? null);
  const emailHeld = effectiveChannel === 'email' && !emailing.open;
  // The message the server suggests, before anybody chooses: held too, where a switch would hold it.
  const suggestedTextHeld =
    preview !== null &&
    (preview.channel === 'email'
      ? !emailPath(me.emailGoLive, preview.contact?.email ?? null).open
      : !textingPath(me.smsGoLive, preview.contact?.phoneE164 ?? null).open);

  /**
   * UNTIL SOMEBODY CHOOSES, THE PATH OFFERED IS THE ONE THAT CAN SUCCEED. She has not agreed, or
   * the switch would hold the text, so the typed reason stands first; she has and it would not, so
   * the message does. A press on either radio, or on either button, settles it for the rest of the
   * form and this never moves it again.
   */
  useEffect(() => {
    if (pathChosen || preview === null) return;
    setHow(preview.mayMessage && !suggestedTextHeld ? 'message' : 'silence');
  }, [pathChosen, preview, suggestedTextHeld]);

  /**
   * The office heard her say yes to emails on the call it is on. One row, and then the preview asks
   * again. Emails only, since ops #51: a yes to texts is the box on the website quote form and
   * nothing a person records, so for a text this is not offered at all.
   */
  async function recordHerConsent() {
    if (!effectiveContactId || effectiveChannel !== 'email') return;
    setConsentProblem(null);
    setRecordingConsent(true);
    try {
      await recordConsent(clientId, effectiveContactId, {
        purposes: ['operational_email'],
        kind: 'granted',
        captureMethod: 'verbal_on_call',
        evidenceText: orNull(consentWords),
      });
      setConsentWords('');
      setPathChosen(true);
      setHow('message');
      setConsentAttempt((n) => n + 1);
    } catch (thrown) {
      setConsentProblem(thrown instanceof ApiError ? thrown.message : 'That was not written down.');
    } finally {
      setRecordingConsent(false);
    }
  }

  function problemsWith(path: Path): string[] {
    const found: string[] = [];
    if (isMove && !isDay(toDate)) found.push('Pick the day the visit is moving to.');
    if (isMove && toDate === visit.scheduledDate && !windowMoved) {
      found.push(
        'That is the day it is already on. To change only the time, choose the arrival window change.',
      );
    }
    if ((isWindow || (isMove && windowMoved)) && !windowValid) {
      found.push('The arrival window needs a start and an end time.');
    }
    if ((isWindow || (isMove && windowMoved)) && windowValid && windowStart >= windowEnd) {
      found.push('The arrival window has to end after it starts.');
    }
    if (isWindow && !windowMoved) found.push('That is the window it already has.');
    if (isCleaner && !cleanerId) found.push('Say who is taking this visit.');
    if (path === 'message') {
      if (!effectiveContactId) found.push('Choose who the message goes to.');
      if (!body.trim()) found.push('The message they will read cannot be empty.');
      if (chosenContact && effectiveChannel === 'sms' && !chosenContact.phoneE164) {
        found.push(
          `${chosenContact.displayName} has no phone number, so a text cannot reach them.`,
        );
      }
      if (chosenContact && effectiveChannel === 'email' && !chosenContact.email) {
        found.push(`${chosenContact.displayName} has no email address on file.`);
      }
      if (textHeld && texting.why) found.push(texting.why);
      if (emailHeld && emailing.why) found.push(emailing.why);
      if (effectiveContactId && preview !== null && !mayMessageNow) {
        found.push(
          effectiveChannel === 'email'
            ? `${chosenContact?.displayName ?? 'This person'} has not agreed to emails, so nothing would be sent. Record what they said, here or on their household page, or say how they already know.`
            : `${chosenContact?.displayName ?? 'This person'} has no website box consent to texts, so nothing would be sent. Send it by email if they agreed to that, or say how they already know.`,
        );
      }
    } else if (!silenceReason) {
      found.push('Say how they already know, or why they cannot be told.');
    }
    return found;
  }

  /**
   * Pressing a button IS the choice of path, so the radio follows the press and the two can never
   * disagree about what is being recorded.
   */
  async function run(path: Path) {
    setFailure(null);
    setPathChosen(true);
    setHow(path);
    const found = problemsWith(path);
    setProblems(found);
    if (found.length > 0) return;

    const change: NewChange = {
      kind,
      note: orNull(note),
      ...(isMove ? { toDate } : {}),
      ...(sendsWindow ? { windowStartLocal: windowStart, windowEndLocal: windowEnd } : {}),
      ...(isCleaner ? { cleanerId } : {}),
      ...(isCancel && chargeDecision && !callOff ? { chargeDecision } : {}),
      ...(callOff ? { callOffId: callOff.id } : {}),
      ...(path === 'message'
        ? {
            message: {
              contactId: effectiveContactId,
              channel: effectiveChannel,
              body: body.trim(),
            },
          }
        : { silenceReason: silenceReason || null }),
    };

    setSaving(true);
    try {
      const result = await recordChange(visit.id, change);
      onChanged(outcomeFor(result));
    } catch (thrown) {
      setFailure(thrown instanceof ApiError ? thrown.message : 'That change was not recorded.');
    } finally {
      setSaving(false);
    }
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    void run(how);
  }

  return (
    <Card className="mb-6 md:mb-0 md:p-3">
      <h2 className="font-serif text-lg">Change this visit</h2>
      <p className="mt-1 mb-3 text-sm text-navy/70">
        The change is written down first. The calendar moves only once they have been told, or once
        you have said how they already know.
      </p>

      <form onSubmit={onSubmit} noValidate>
        {failure ? (
          <div className="mb-3">
            <ErrorNotice title="Not recorded" message={failure} />
          </div>
        ) : null}
        {problems.length > 0 ? (
          <div
            className="mb-3 rounded-xl border border-coral/50 border-l-4 border-l-coral bg-parchment p-3"
            role="alert"
          >
            <ul className="list-disc space-y-1 pl-5 text-sm">
              {problems.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className={PAIR}>
          <SelectField
            id="change-kind"
            label="What is changing"
            value={kind}
            onChange={setKind}
            options={actions}
            toLabel={(value) => VISIT_CHANGE_KIND_LABELS[value]}
            hint={
              noAccess
                ? 'Nobody could get in, so the one thing to do with this visit is put it back on the calendar.'
                : undefined
            }
          />

          {isMove ? (
            <TextField
              id="change-to-date"
              label="Moving to"
              type="date"
              required
              value={toDate}
              onChange={setToDate}
            />
          ) : null}

          {isCleaner ? (
            crewProblem ? (
              <div className="mb-3">
                <ErrorNotice
                  title="The crew did not load"
                  message={crewProblem}
                  onRetry={() => setCrewAttempt((n) => n + 1)}
                />
              </div>
            ) : crew === null ? (
              <Busy what="the crew" />
            ) : (
              <SelectField
                id="change-cleaner"
                label="Who is taking it"
                required
                value={cleanerId}
                onChange={setCleanerId}
                options={crew.map((cleaner) => cleaner.id)}
                toLabel={(id) => crew.find((cleaner) => cleaner.id === id)?.displayName ?? id}
                placeholder={
                  callOff
                    ? 'Choose somebody marked as having completed the checks'
                    : 'Choose somebody on the crew'
                }
              />
            )
          ) : null}
        </div>

        {isMove || isWindow ? (
          <div className="grid grid-cols-2 gap-3">
            <TimeField
              id="change-window-start"
              label="Arrives from"
              value={windowStart}
              onChange={setWindowStart}
            />
            <TimeField
              id="change-window-end"
              label="Arrives by"
              value={windowEnd}
              onChange={setWindowEnd}
            />
          </div>
        ) : null}
        {isMove ? (
          <p className="-mt-2 mb-4 text-xs text-navy/65">
            Leave the times as they are to keep the same window on the new day.
          </p>
        ) : null}

        {isCancel && callOff ? (
          <p className="mb-4 text-sm text-navy/75">
            Skipped because a cleaner could not come, so it is never charged.
          </p>
        ) : isCancel ? (
          <SelectField
            id="change-charge"
            label="Is the cancellation charged"
            value={chargeDecision}
            onChange={setChargeDecision}
            options={CHARGE_DECISIONS}
            toLabel={(value) => CHARGE_DECISION_LABELS[value]}
            placeholder="Decide later"
            hint="Published policy: no charge with more than a day's notice, no charge the first time under it, and half the rate after that, told before rather than after."
          />
        ) : null}

        <TextAreaField
          id="change-note"
          label="A note for the record"
          value={note}
          onChange={setNote}
          rows={2}
          hint="In your words, for the client record and your own memory. They do not read this."
        />

        <h3 className="mt-2 mb-2 font-serif text-lg">How will they know?</h3>

        {me.canHandOffMessages ? (
          <div className="mb-3 grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-3">
            <HowChoice
              id="how-message"
              checked={how === 'message'}
              onChoose={() => {
                setPathChosen(true);
                setHow('message');
              }}
              label="Send them a message"
              detail="You approve the sentence."
            />
            <HowChoice
              id="how-silence"
              checked={how === 'silence'}
              onChoose={() => {
                setPathChosen(true);
                setHow('silence');
              }}
              label="They already know"
              detail="Say how, on the record."
            />
          </div>
        ) : (
          <p className="mb-3 text-sm">
            The message link to the phone system is not connected yet, so record how they were told.
          </p>
        )}

        {/* On a phone only one path is drawn, so the reason the other one is not the one offered
            has to be said here rather than inside a panel nobody can see. The household page is
            where what she agreed to is kept, so it is named rather than described. */}
        {me.canHandOffMessages && preview !== null && !mayMessageNow ? (
          effectiveChannel === 'email' ? (
            <p className="mb-3 text-sm text-navy/70">
              {chosenContact?.displayName ?? 'This person'} has not agreed to {channelWord} about
              their cleans, so a message would be refused. Write down what they said here, or on
              their{' '}
              <Link to={`/clients/${clientId}`} className="font-medium underline">
                household page
              </Link>
              , where the record of it is kept.
            </p>
          ) : (
            <p className="mb-3 text-sm text-navy/70">
              {chosenContact?.displayName ?? 'This person'} has not ticked the box for texts on the
              website quote form, so a text would be refused. Say how they already know, or send it
              by email if they agreed to that.
            </p>
          )
        ) : null}

        {/* WHERE OPS'S GO-LIVE SWITCH STANDS, on the text path, drawn outside the message panel for
            the same reason: on a phone the typed reason is what is drawn when it holds the text. */}
        {me.canHandOffMessages && effectiveChannel === 'sms' ? (
          <TextingGate
            path={texting}
            wayOn="Send it by email if they agreed to that, or say how they already know."
          />
        ) : null}
        {/* And its switch for client email, on the email path, drawn there for the same reason. */}
        {me.canHandOffMessages && effectiveChannel === 'email' ? (
          <EmailGate path={emailing} wayOn="Say how they already know." />
        ) : null}

        {showMessage ? (
          <div
            className={`mb-3 rounded-lg border p-3 ${
              how === 'message' ? 'border-navy/25' : 'border-navy/15'
            }`}
          >
            <div className={PAIR}>
              <SelectField
                id="message-contact"
                label="Who to tell"
                required
                value={effectiveContactId}
                onChange={setContactId}
                options={contacts.map((contact) => contact.id)}
                toLabel={(id) => {
                  const contact = contacts.find((row) => row.id === id);
                  return contact
                    ? `${contact.displayName} (${humanise(contact.role).toLowerCase()})`
                    : id;
                }}
                placeholder="Choose the person"
              />
              <SelectField
                id="message-channel"
                label="How"
                value={effectiveChannel}
                onChange={setChannel}
                options={SEND_CHANNELS}
                toLabel={(value) => MESSAGE_CHANNEL_LABELS[value]}
                hint={
                  preview?.channel === 'call'
                    ? 'They asked to be called rather than texted. If you ring them, choose the other answer above instead.'
                    : undefined
                }
              />
            </div>
            {previewProblem ? (
              <div className="mb-3">
                <ErrorNotice title="The default sentence did not load" message={previewProblem} />
              </div>
            ) : null}

            {/* THE REGISTER SAYS NO TO A TEXT, AND NOTHING SAID HERE CHANGES THAT (ops #51). A yes
                to texts is the box on the website quote form alone, so the panel says so and the
                typed reason beside it is the way on. */}
            {preview !== null &&
            effectiveContactId &&
            !mayMessageNow &&
            effectiveChannel !== 'email' ? (
              <div
                id="texts-need-the-box"
                className="mb-3 rounded-lg border border-coral/50 border-l-4 border-l-coral bg-parchment p-3"
              >
                <p className="text-sm">
                  {chosenContact?.displayName ?? 'This person'} has not ticked the box for texts on
                  the website quote form, so no text would go. Say how they already know below, or
                  choose email if they agreed to that.
                </p>
                <p className="mt-1 text-xs text-navy/65">{TEXT_CONSENT_NOTE}</p>
              </div>
            ) : null}

            {/* THE REGISTER SAYS NO TO AN EMAIL, AND THE WAY OUT IS TO ASK HER. Ops holds the consent
                record, append-only, and a client saying yes to emails on a phone call is recorded
                here, in her words, at the moment it is heard. */}
            {preview !== null &&
            effectiveContactId &&
            !mayMessageNow &&
            effectiveChannel === 'email' ? (
              <div className="mb-3 rounded-lg border border-coral/50 border-l-4 border-l-coral bg-parchment p-3">
                <p className="text-sm">
                  {chosenContact?.displayName ?? 'This person'} has not agreed to {channelWord}, so
                  nothing would go. If they have just said yes on the phone, write it down here and
                  it can go.
                </p>
                {consentProblem ? (
                  <div className="mt-2">
                    <ErrorNotice title="Not written down" message={consentProblem} />
                  </div>
                ) : null}
                <div className="mt-2">
                  <TextAreaField
                    id="consent-words"
                    label="Their words"
                    value={consentWords}
                    onChange={setConsentWords}
                    rows={2}
                    hint="What they actually said. For a consent given on a call this is the only evidence there will ever be."
                  />
                </div>
                <Button
                  tone="quiet"
                  full
                  disabled={recordingConsent}
                  onClick={() => void recordHerConsent()}
                >
                  {recordingConsent
                    ? 'Writing it down'
                    : `They said yes to ${channelWord} about their cleans`}
                </Button>
              </div>
            ) : null}

            {/* WHAT THEIR CHOICES WOULD DO TO IT (ops #76 phase 2): their own row, or the office's
                hold, would park it, and the change waits for a typed reason, as for any refusal; and
                where no way is open to the visit contact, the office is given the call. */}
            {preview !== null && effectiveContactId && mayMessageNow && chosenLine !== null ? (
              <div
                id="message-choice"
                className="mb-3 rounded-lg border border-coral/50 border-l-4 border-l-coral bg-parchment p-3"
              >
                <p className="text-sm wrap-anywhere">{chosenLine}</p>
                <p className="mt-1 text-xs text-navy/65">
                  The change is recorded either way, and the visit moves once you say how they were
                  told.
                </p>
              </div>
            ) : null}

            <TextAreaField
              id="message-body"
              label="What they will read"
              value={body}
              onChange={setBody}
              rows={4}
              hint="Written by this system from the facts above, never by anything that guesses. Edit it as you like; what you approve is what goes."
            />
            <Button
              type={how === 'message' ? 'submit' : 'button'}
              full
              disabled={saving || textHeld || emailHeld}
              onClick={() => void run('message')}
            >
              {saving && how === 'message' ? 'Recording' : 'Record the change and send the message'}
            </Button>
          </div>
        ) : null}

        {showSilence ? (
          <div
            className={`rounded-lg border p-3 ${
              how === 'silence' ? 'border-navy/25' : 'border-navy/15'
            }`}
          >
            <SelectField
              id="silence-reason"
              label="How they know"
              required
              value={silenceReason}
              onChange={setSilenceReason}
              options={SILENCE_REASONS}
              toLabel={(value) => SILENCE_REASON_LABELS[value]}
              placeholder="Choose one"
            />
            <Button
              type={how === 'silence' ? 'submit' : 'button'}
              full
              disabled={saving}
              onClick={() => void run('silence')}
            >
              {saving && how === 'silence'
                ? 'Recording'
                : isCancel
                  ? 'Record the cancellation'
                  : 'Move it now'}
            </Button>
          </div>
        ) : null}
      </form>
    </Card>
  );
}
